"use client";

import { Timeline } from "#/components/ui/timeline";
import { experiences, formations } from "#/data";
import { Badge } from "#/components/ui/badge";
import { Button } from "#/components/ui/button";
import { GraduationCap, Briefcase, MapPin, Globe, ChevronRight } from "lucide-react";
import { AchievementsDrawer } from "#/components/achievements-drawer";
import { useState } from "react";
import dynamic from "next/dynamic";

const Section = dynamic(() => import("./section"), {
  ssr: false,
});

export function TimelineSection() {
  const [selectedExperience, setSelectedExperience] = useState<(typeof experiences)[0] | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Combine and sort experiences and formations by date (most recent first)
  const timelineData = [
    ...experiences.map((exp) => ({
      type: "experience",
      id: exp.id,
      title: exp.title,
      subtitle: exp.company,
      location: exp.location,
      startDate: new Date(exp.startDate),
      endDate: exp.endDate === "present" ? new Date() : new Date(exp.endDate),
      description: exp.description,
      details: exp,
      isRemote: exp.isRemote,
    })),
    ...formations.map((form) => ({
      type: "formation",
      id: form.id,
      title: form.title,
      subtitle: form.school,
      location: form.location,
      startDate: new Date(form.startDate),
      endDate: new Date(form.endDate),
      description: form.description,
      details: form,
      isRemote: false,
    })),
  ].sort((a, b) => b.endDate.getTime() - a.endDate.getTime());

  // Format date to display month and year
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("fr-FR", { year: "numeric", month: "short" });
  };

  const openAchievementsDrawer = (experience: (typeof experiences)[0]) => {
    setSelectedExperience(experience);
    setDrawerOpen(true);
  };

  return (
    <Section id="timeline" title="Parcours professionnel et formation">
      <Timeline
        data={timelineData.map((item) => ({
          title: formatDate(item.startDate) + " - " + (item.endDate ? formatDate(item.endDate) : "Présent"),
          content: (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <h3 className="text-lg md:text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                    {item.title}
                  </h3>
                  <Badge variant={item.type === "experience" ? "default" : "secondary"} className="ml-2">
                    {item.type === "experience" ? (item.details as (typeof experiences)[0]).type : "Formation"}
                  </Badge>
                </div>

                <div
                  className="flex flex-col md:flex-row md:items-center
                gap-2 md:gap-4 text-sm text-neutral-700 dark:text-neutral-300"
                >
                  <div className="flex items-center gap-1">
                    {item.type === "experience" ? (
                      <Briefcase className="h-4 w-4" />
                    ) : (
                      <GraduationCap className="h-4 w-4" />
                    )}
                    <span>{item.subtitle}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{item.location}</span>
                  </div>

                  {item.isRemote && (
                    <div className="flex items-center gap-1">
                      <Globe className="h-4 w-4" />
                      <span>Remote</span>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-neutral-700 dark:text-neutral-300 text-sm">{item.description}</p>

              {item.type === "experience" && (item.details as (typeof experiences)[0]).skills.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {(item.details as (typeof experiences)[0]).skills.map((skill, index) => (
                    <Badge key={index} variant="outline" className="bg-neutral-100 dark:bg-neutral-800">
                      {skill}
                    </Badge>
                  ))}
                </div>
              )}

              {item.type === "experience" && (item.details as (typeof experiences)[0]).achievements.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium mb-2 text-neutral-900 dark:text-neutral-100">Réalisations</h4>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-neutral-700 dark:text-neutral-300">
                      {(item.details as (typeof experiences)[0]).achievements.length} réalisations notables
                    </p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center gap-1 text-sm text-neutral-700 dark:text-neutral-300
                      hover:text-neutral-900 dark:hover:text-neutral-100"
                      onClick={() => openAchievementsDrawer(item.details as (typeof experiences)[0])}
                    >
                      Voir plus <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {item.type === "experience" && (item.details as (typeof experiences)[0]).impact && (
                <div
                  className="mt-4 p-3 bg-neutral-50 dark:bg-neutral-900 rounded-md border
                border-neutral-200 dark:border-neutral-800"
                >
                  <h4 className="text-sm font-medium mb-1 text-neutral-900 dark:text-neutral-100">Impact</h4>
                  <p className="text-sm text-neutral-700 dark:text-neutral-300">
                    {(item.details as (typeof experiences)[0]).impact}
                  </p>
                </div>
              )}
            </div>
          ),
          icon: item.type === "experience" ? "briefcase" : "education",
        }))}
      />

      <AchievementsDrawer experience={selectedExperience} open={drawerOpen} onOpenChange={setDrawerOpen} />
    </Section>
  );
}
